import { useMemo, useState } from 'react';
import { formatDate, formatTime } from '../../../lib/format';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AppointmentRow, CursorPage, EmployeeRow, Plant } from '@dru/types';
import { APPOINTMENT_STATUS_LABEL, EXAM_TYPE_LABEL } from '@dru/types';
import {
  Alert, Button, Card, CardHeader, DataTable, EmptyState, EntityCombobox, FilterBar, IconAction, Input,
  RowActions, StatusBadge, type Column, type Tone, cn,
} from '@dru/ui';
import { CalendarClock, CalendarDays, CalendarPlus, Eye, List, UserX } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { useToast } from '@dru/ui';
import { useQuery } from '../../../lib/useQuery';
import { NoShowDialog, RescheduleDialog } from '../components/AppointmentActions';
import { ArrangeDialog } from '../components/ArrangeDialog';

/**
 * What is booked, and what happened to it.
 *
 * Two views over one query. The list is the worklist — sortable by the thing
 * that matters, which is when — and the calendar is for the question a list
 * answers badly: is next Tuesday overloaded. Both hit the same endpoint with
 * the same filters, so a filtered list and a filtered calendar never disagree.
 *
 * HR can create, move and cancel a slot — moving and cancelling from the
 * appointment's own page. HR cannot check anyone in: that
 * happens at the venue with the worker present and an identity check, and the
 * permission for it is held by the hospital, not by the company.
 */

const tone = (status: string): Tone =>
  status === 'COMPLETED' ? 'covered'
  : status === 'CHECKED_IN' ? 'info'
  : status === 'NO_SHOW' ? 'overdue'
  : status === 'CANCELLED' || status === 'RESCHEDULED' ? 'neutral'
  : 'pending';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const time = (s: string) => formatTime(s);

/** The windows HR actually asks about, one tap each. Any other is the From/To pair. */
type Preset = 'today' | 'week' | 'fortnight' | 'past';
const PRESETS: Array<[Preset, string]> = [
  ['today', 'Today'], ['week', 'Next 7 days'], ['fortnight', 'Next 14 days'], ['past', 'Last 30 days'],
];
function windowOf(p: Preset, today: Date): { from: string; to: string } {
  switch (p) {
    case 'today': return { from: iso(today), to: iso(today) };
    case 'week': return { from: iso(today), to: iso(addDays(today, 6)) };
    case 'fortnight': return { from: iso(today), to: iso(addDays(today, 13)) };
    case 'past': return { from: iso(addDays(today, -30)), to: iso(today) };
  }
}

export function Appointments() {
  const nav = useNavigate();
  const { permissions } = useAuth();
  const { toast } = useToast();
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const today = useMemo(() => new Date(), []);

  // Arriving from the dashboard's "open no-shows" tile: a no-show is in the
  // past by definition, and the default window starts today — so the list
  // HR was sent to was empty. `past=1` looks back instead.
  const [params] = useSearchParams();
  const past = params.get('past') === '1';
  const [from, setFrom] = useState(iso(past ? addDays(today, -90) : today));
  const [to, setTo] = useState(iso(past ? today : addDays(today, 13)));
  const [status, setStatus] = useState(params.get('status') ?? '');
  const [plantId, setPlantId] = useState('');
  // Searched on the server by the employee's id: the list endpoint has no
  // free-text search, and filtering the loaded page would miss rows past it.
  const [employee, setEmployee] = useState<EmployeeRow | null>(null);
  const preset = PRESETS.find(([k]) => {
    const w = windowOf(k, today);
    return w.from === from && w.to === to;
  })?.[0] ?? null;
  const pickPreset = (k: Preset) => { const w = windowOf(k, today); setFrom(w.from); setTo(w.to); };
  const [moving, setMoving] = useState<{ row: AppointmentRow; mode: 'move' | 'rebook' } | null>(null);
  const [marking, setMarking] = useState<AppointmentRow | null>(null);

  const canRead = permissions.has('appointment.read');
  // Booking raises the medical case, routes it to the lab HR picks and books
  // the slot, in one dialog (docs/38 D5).
  const canBook = permissions.has('examination.create');
  const [booking, setBooking] = useState(false);
  const canPlants = permissions.has('plant.read');
  const plants = useQuery<CursorPage<Plant> | null>(
    () => (canPlants ? api.get<CursorPage<Plant>>('/plants?limit=100') : Promise.resolve(null)), [canPlants]);
  const plantList = plants.data?.items ?? [];

  const query = new URLSearchParams({ limit: '100', from, to });
  if (status) query.set('status', status);
  if (plantId) query.set('plantId', plantId);
  if (employee) query.set('employeeId', employee.id);

  const page = useQuery<CursorPage<AppointmentRow> | null>(
    () => (canRead ? api.get<CursorPage<AppointmentRow>>(`/appointments?${query.toString()}`) : Promise.resolve(null)),
    [from, to, status, plantId, employee?.id, canRead],
  );

  const rows = page.data?.items ?? [];
  const badRange = from > to;

  // What went by without the worker being seen — missed and still to rebook,
  // or past its slot with nobody checked in. Asked for separately and with no
  // dates: every one of these is behind today, and the window above starts
  // today, so the list on its own showed three bookings while the dashboard
  // counted ten.
  const overdue = useQuery<CursorPage<AppointmentRow> | null>(
    () => (canRead
      ? api.get<CursorPage<AppointmentRow>>(`/appointments?attention=1&limit=100${plantId ? `&plantId=${plantId}` : ''}${employee ? `&employeeId=${employee.id}` : ''}`)
      : Promise.resolve(null)),
    [plantId, employee?.id, canRead],
  );
  const overdueRows = overdue.data?.items ?? [];
  const reloadAll = () => { page.reload(); overdue.reload(); };

  /** A booked slot whose time has gone, with nobody checked in. */
  const passed = (a: AppointmentRow) =>
    ['SCHEDULED', 'CONFIRMED'].includes(a.status) && new Date(a.scheduledFor).getTime() < Date.now();

  const columns: Column<AppointmentRow>[] = [
    {
      key: 'employee', header: 'Employee',
      cell: (a) => (a.order?.employee ? (
        <div>
          <div className="font-medium text-neutral-900">{a.order.employee.fullName}</div>
          <div className="font-mono text-caption text-neutral-500">{a.order.employee.employeeCode}</div>
        </div>
      ) : <span className="text-neutral-500">—</span>),
    },
    {
      key: 'when', header: 'When',
      cell: (a) => (
        <div>
          <div className="flex items-center gap-1.5 text-neutral-900">
            {formatDate(a.scheduledFor)}
            {iso(new Date(a.scheduledFor)) === iso(new Date()) && (
              <span className="rounded-badge bg-primary-50 px-1.5 py-0.5 text-[11px] font-semibold text-primary-700">Today</span>
            )}
          </div>
          <div className="text-caption tabular-nums text-neutral-500">
            {time(a.scheduledFor)} · {a.slotMinutes} min
          </div>
        </div>
      ),
    },
    {
      key: 'venue', header: 'Venue',
      cell: (a) => (
        <div>
          <div className="text-neutral-800">{a.partner?.name ?? '—'}</div>
          {a.venueType === 'CAMP' && <div className="text-caption text-neutral-500">Camp</div>}
        </div>
      ),
    },
    {
      key: 'exam', header: 'Medical case', hideOnMobile: true,
      cell: (a) => (
        <span className="text-small text-neutral-600">
          {a.order ? (EXAM_TYPE_LABEL[a.order.examinationType] ?? a.order.examinationType) : '—'}
        </span>
      ),
    },
    {
      key: 'status', header: 'Status', mobileLabel: false,
      // "Scheduled" over a slot that has already gone is the status nobody
      // updated, not the state of the booking.
      cell: (a) => (passed(a)
        ? <StatusBadge tone="dueSoon">Slot passed — not checked in</StatusBadge>
        : (
          <StatusBadge tone={tone(a.status)}>
            {a.status === 'NO_SHOW' && a.order?.status === 'NO_SHOW_OPEN'
              ? 'Missed — to rebook'
              : APPOINTMENT_STATUS_LABEL[a.status] ?? a.status}
          </StatusBadge>
        )),
    },
    {
      // One action per row, and only the one the slot is waiting for.
      // Moving and cancelling are on the appointment's own page, a
      // click away, rather than four buttons on every row.
      key: 'actions', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
      cell: (a) => {
        const who = a.order?.employee?.fullName ?? 'this appointment';
        return (
          <RowActions>
            <IconAction label={`View appointment for ${who}`} icon={Eye} onClick={() => nav(`/hr/appointments/${a.id}`)} />
            {a.status === 'NO_SHOW' && a.order?.status === 'NO_SHOW_OPEN' && permissions.has('appointment.reschedule') ? (
              <IconAction label={`Rebook ${who}`} icon={CalendarClock} primary onClick={() => setMoving({ row: a, mode: 'rebook' })} />
            ) : passed(a) && permissions.has('appointment.noshow') ? (
              <IconAction label={`Mark ${who} as did not attend`} icon={UserX} danger onClick={() => setMarking(a)} />
            ) : null}
          </RowActions>
        );
      },
    },
  ];

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Appointments" />
        <EmptyState title="Appointments are not part of your role" hint="Ask your company administrator if you need to follow bookings." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Appointments"
        hint="Slots booked at your empanelled venues and camps. Check-in happens at the venue."
        actions={(
          <div className="flex flex-wrap items-center gap-2">
          {canBook && (
            <Button onClick={() => setBooking(true)}>
              <CalendarPlus aria-hidden className="h-4 w-4" /> Schedule checkup
            </Button>
          )}
          <div className="inline-flex rounded-input border border-neutral-300 bg-white p-0.5" role="group" aria-label="View">
            {([['list', List, 'List'], ['calendar', CalendarDays, 'Calendar']] as const).map(([v, Icon, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={cn(
                  'inline-flex h-9 max-md:h-10 items-center gap-1.5 rounded px-3 text-small font-medium transition-colors',
                  view === v ? 'bg-primary-600 text-white' : 'text-neutral-700 hover:bg-neutral-50',
                )}
              >
                <Icon aria-hidden className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          </div>
        )}
      />

      {/* Who and when first: the two questions somebody opens this page with. */}
      <div className="flex flex-wrap items-end gap-3">
        <EntityCombobox<EmployeeRow>
          label="Employee"
          className="w-full sm:w-80"
          value={employee}
          onChange={setEmployee}
          fetchOptions={async (search, cursor) => {
            const pg = await api.get<CursorPage<EmployeeRow>>(
              `/employees?limit=20&q=${encodeURIComponent(search)}` + (cursor ? `&cursor=${cursor}` : ''));
            return { items: pg.items, nextCursor: pg.nextCursor };
          }}
          getKey={(e) => e.id}
          placeholder="All employees"
          searchPlaceholder="Name or employee ID"
          emptyMessage="No employee matches that."
          renderOption={(e) => (
            <>
              <span className="block font-medium text-neutral-900">{e.fullName}</span>
              <span className="block font-mono text-caption font-normal text-neutral-500">{e.employeeCode}</span>
            </>
          )}
          renderValue={(e) => `${e.fullName} — ${e.employeeCode}`}
        />
        <div role="group" aria-label="Date range" className="flex gap-1.5 max-md:-mx-4 max-md:w-[calc(100%+2rem)] max-md:overflow-x-auto max-md:px-4 max-md:pb-1">
          {PRESETS.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={preset === k} onClick={() => pickPreset(k)}
              className={cn(
                'inline-flex h-10 shrink-0 items-center rounded-full border px-3.5 text-small font-medium transition-colors',
                preset === k ? 'border-primary-600 bg-primary-600 text-white' : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50',
              )}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Then what to narrow it by, and any other window. */}
      <FilterBar
        filters={[
          {
            key: 'status', label: 'Status', allLabel: 'Any status',
            options: Object.entries(APPOINTMENT_STATUS_LABEL).map(([value, label]) => ({ value, label })),
          },
          ...(plantList.length > 1 ? [{
            key: 'plantId', label: 'Plant', allLabel: 'All plants',
            options: plantList.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` })),
          }] : []),
        ]}
        more={(
          <>
            <label className="inline-flex items-center gap-2 text-small text-neutral-700">
              From
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-auto py-0" invalid={badRange} />
            </label>
            <label className="inline-flex items-center gap-2 text-small text-neutral-700">
              To
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-auto py-0" invalid={badRange} />
            </label>
          </>
        )}
        value={{ status: status || undefined, plantId: plantId || undefined }}
        onChange={(v) => { setStatus(v.status ?? ''); setPlantId(v.plantId ?? ''); }}
      >
        {/* Folded on a phone, so the window still has to be said. */}
        <span className="text-small text-neutral-600 md:hidden">{formatDate(from)} – {formatDate(to)}</span>
      </FilterBar>
      {badRange && <Alert tone="warning">The start date is after the end date. Choose a start on or before the end.</Alert>}
      {view === 'list' && page.data && !badRange && (
        <p className="text-small text-neutral-600" aria-live="polite">
          {page.data.nextCursor ? `Showing the first ${rows.length}` : `${rows.length} appointment${rows.length === 1 ? '' : 's'}`}
          {' '}· {from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`}
          {employee ? ` · ${employee.fullName}` : ''}
          {page.data.nextCursor ? '. Narrow the dates to see the rest.' : ''}
        </p>
      )}

      {/* Above the dated list, whatever its window says. */}
      {view === 'list' && !status && overdueRows.length > 0 && (
        <Card className="min-w-0">
          <CardHeader
            title={`${overdueRows.length} booking${overdueRows.length === 1 ? '' : 's'} went by without the employee being seen`}
            hint="Missed appointments to rebook, and slots that have passed with nobody checked in. The venue can still check a late employee in the day after the slot; after that the case moves to rebooking on its own."
          />
          <DataTable<AppointmentRow>
            rows={overdueRows}
            loading={overdue.loading}
            error={overdue.error}
            onRetry={overdue.reload}
            rowKey={(a) => a.id}
            onRowClick={(a) => nav(`/hr/appointments/${a.id}`)}
            columns={columns}
          />
        </Card>
      )}

      {view === 'list' ? (
        <DataTable<AppointmentRow>
          rows={rows}
          loading={page.loading}
          error={page.error}
          onRetry={page.reload}
          rowKey={(a) => a.id}
          onRowClick={(a) => nav(`/hr/appointments/${a.id}`)}
          empty={{
            title: employee ? `No appointments for ${employee.fullName} in this range` : 'No appointments in this range',
            hint: canBook
              ? 'Schedule a checkup for an employee, or widen the dates.'
              : 'Widen the dates to see other bookings.',
            action: canBook ? <Button onClick={() => setBooking(true)}>Schedule checkup</Button> : undefined,
          }}
          columns={columns}
        />
      ) : (
        <CalendarView from={from} to={to} rows={rows} loading={page.loading}
          onPickDay={(d) => { setFrom(d); setTo(d); setView('list'); }} />
      )}

      {moving && (
        <RescheduleDialog
          appointmentId={moving.row.id}
          partnerId={moving.row.partnerId}
          partnerName={moving.row.partner?.name}
          who={moving.row.order?.employee?.fullName}
          scheduledFor={moving.row.scheduledFor}
          mode={moving.mode}
          onClose={() => setMoving(null)}
          onDone={() => { setMoving(null); reloadAll(); toast('success', moving.mode === 'rebook' ? 'Rebooked.' : 'Appointment moved.'); }}
        />
      )}
      {booking && (
        <ArrangeDialog
          onClose={() => setBooking(false)}
          onDone={(orderId) => {
            setBooking(false);
            reloadAll();
            if (orderId) {
              toast('success', 'Checkup saved. Its progress is on the case page.');
              nav(`/hr/examinations/${orderId}`);
            }
          }}
        />
      )}
      {marking && (
        <NoShowDialog
          appointmentId={marking.id}
          who={marking.order?.employee?.fullName}
          when={marking.scheduledFor}
          onClose={() => setMarking(null)}
          onDone={() => { setMarking(null); reloadAll(); toast('info', 'Recorded as not attended. The case is on the rebooking list.'); }}
        />
      )}
    </div>
  );
}

/**
 * The calendar.
 *
 * Deliberately a plain day grid over the chosen range rather than a fixed
 * month: the filters above already decide the window, and a month view would
 * either ignore them or fight them. Capacity is the question it answers, so
 * the count per day is the emphasised figure and the names are secondary.
 */
function CalendarView({
  from, to, rows, loading, onPickDay,
}: { from: string; to: string; rows: AppointmentRow[]; loading: boolean; onPickDay: (day: string) => void }) {
  const days = useMemo(() => {
    const start = new Date(from);
    const end = new Date(to);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
    // Capped so a year-wide filter does not render 365 cells.
    const out: Date[] = [];
    for (let d = new Date(start); d <= end && out.length < 62; d = addDays(d, 1)) {
      out.push(new Date(d));
    }
    return out;
  }, [from, to]);

  const byDay = useMemo(() => {
    const m = new Map<string, AppointmentRow[]>();
    for (const a of rows) {
      const key = iso(new Date(a.scheduledFor));
      const list = m.get(key);
      if (list) list.push(a); else m.set(key, [a]);
    }
    for (const list of m.values()) {
      list.sort((x, y) => x.scheduledFor.localeCompare(y.scheduledFor));
    }
    return m;
  }, [rows]);

  if (!days.length) {
    return <Alert tone="warning">Choose a start date on or before the end date.</Alert>;
  }

  const todayKey = iso(new Date());

  return (
    <Card>
      <CardHeader
        title={`${days.length} day${days.length === 1 ? '' : 's'}`}
        hint="Counts per day, so an overloaded date is visible before it arrives."
      />
      <div
        className={cn(
          'grid gap-px bg-neutral-200 p-px',
          'grid-cols-2 sm:grid-cols-3 lg:grid-cols-7',
          loading && 'opacity-60',
        )}
      >
        {days.map((d) => {
          const key = iso(d);
          const list = byDay.get(key) ?? [];
          const live = list.filter((a) => !['CANCELLED', 'RESCHEDULED'].includes(a.status));
          return (
            <button type="button" key={key} onClick={() => onPickDay(key)} title="Open this day as a list"
              aria-label={`${d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}: ${live.length} appointment${live.length === 1 ? '' : 's'}. Open as a list.`}
              className="min-h-[7rem] bg-white p-2 text-left hover:bg-neutral-50">
              <div className="flex items-baseline justify-between">
                <span
                  className={cn(
                    'text-xs font-medium',
                    key === todayKey ? 'text-primary-700' : 'text-neutral-500',
                  )}
                >
                  {d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' })}
                </span>
                {live.length > 0 && (
                  <span className="text-sm font-semibold tabular-nums text-neutral-900">
                    {live.length}
                  </span>
                )}
              </div>

              <ul className="mt-1.5 space-y-1">
                {list.slice(0, 3).map((a) => (
                  <li key={a.id} className="truncate text-xs text-neutral-600">
                    <span className="tabular-nums text-neutral-500">{time(a.scheduledFor)}</span>{' '}
                    {a.order?.employee?.fullName ?? '—'}
                  </li>
                ))}
                {list.length > 3 && (
                  <li className="text-xs text-neutral-500">+{list.length - 3} more</li>
                )}
              </ul>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
